import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { brl, fdatetime, todayISO, daysAgoISO } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { Plus, Trash2, Loader2 } from "lucide-react";

const CATS = { entrada: ["Aporte", "Outros"], saida: ["Sangria", "Fornecedor", "Despesa", "Outros"] };
const ROLE_LABELS = { admin: "Administrador", gerente: "Gerente", recepcao: "Recepção", professor: "Professor" };

function MovementDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const [type, setType] = useState("saida");
  const [category, setCategory] = useState("Sangria");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [pmId, setPmId] = useState("");
  const [saving, setSaving] = useState(false);
  const { data: pms } = useQuery({
    queryKey: ["payment-methods"],
    queryFn: async () => (await api.get("/payment-methods")).data,
  });

  const save = async () => {
    const value = parseFloat(String(amount).replace(",", "."));
    if (!value || value <= 0) {
      toast.error("Informe um valor válido");
      return;
    }
    setSaving(true);
    try {
      await api.post("/movements", {
        type, category, description, amount: value,
        payment_method_id: pmId || null,
      });
      toast.success(`${type === "entrada" ? "Entrada" : "Saída"} registrada — ${brl(value)}`);
      qc.invalidateQueries();
      onOpenChange(false);
      setAmount("");
      setDescription("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="movement-dialog">
        <DialogHeader>
          <DialogTitle>Nova movimentação</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {["entrada", "saida"].map((t) => (
              <button
                key={t}
                data-testid={`movement-type-${t}`}
                onClick={() => {
                  setType(t);
                  setCategory(CATS[t][0]);
                }}
                className={cn(
                  "rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors",
                  type === t
                    ? t === "entrada"
                      ? "border-emerald-500 bg-emerald-500/10 text-emerald-500"
                      : "border-red-500 bg-red-500/10 text-red-500"
                    : "border-border text-muted-foreground hover:bg-accent"
                )}
              >
                {t === "entrada" ? "Entrada" : "Saída"}
              </button>
            ))}
          </div>
          <div className="space-y-2">
            <Label>Categoria</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="movement-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATS[type].map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Valor (R$)</Label>
            <Input
              data-testid="movement-amount"
              inputMode="decimal"
              placeholder="0,00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="num"
            />
          </div>
          <div className="space-y-2">
            <Label>Forma de pagamento</Label>
            <Select value={pmId} onValueChange={setPmId}>
              <SelectTrigger data-testid="movement-pm">
                <SelectValue placeholder="Dinheiro (padrão)" />
              </SelectTrigger>
              <SelectContent>
                {(pms || []).filter((p) => p.active).map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Descrição</Label>
            <Input data-testid="movement-description" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <Button data-testid="movement-save" onClick={save} disabled={saving} className="w-full h-11">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SettleDialog({ sale, onClose }) {
  const qc = useQueryClient();
  const [pmId, setPmId] = useState("");
  const [sellerId, setSellerId] = useState("");
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const { data: pms } = useQuery({
    queryKey: ["payment-methods"],
    queryFn: async () => (await api.get("/payment-methods")).data,
  });
  const { data: sellers } = useQuery({
    queryKey: ["sellers"],
    queryFn: async () => (await api.get("/sellers")).data,
  });

  useEffect(() => {
    if (sale) {
      setAmount(String(sale.pending_amount).replace(".", ","));
      setPmId("");
      setSellerId("");
    }
  }, [sale]);

  const save = async () => {
    const value = parseFloat(String(amount).replace(",", "."));
    if (!value || value <= 0 || !pmId) {
      toast.error("Informe o valor e a forma de pagamento");
      return;
    }
    if ((sellers || []).filter((s) => s.active).length > 0 && !sellerId) {
      toast.error("Selecione o vendedor responsável pelo recebimento");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(`/sales/${sale.id}/settle`, {
        payments: [{ payment_method_id: pmId, amount: value }],
        seller_id: sellerId || null,
        seller_name: (sellers || []).find((s) => s.id === sellerId)?.name || null,
      });
      toast.success(r.data.pending_amount > 0 ? `Recebido. Ainda faltam ${brl(r.data.pending_amount)}` : "Venda quitada");
      qc.invalidateQueries();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!sale} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="settle-dialog">
        <DialogHeader>
          <DialogTitle>Receber pendência</DialogTitle>
        </DialogHeader>
        {sale && (
          <div className="space-y-4">
            <div className="rounded-lg bg-muted/60 px-4 py-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Venda</span>
                <span className="font-medium">{sale.items.map((i) => i.name).join(", ")}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total</span>
                <span className="num">{brl(sale.total)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Falta receber</span>
                <span className="num font-semibold text-amber-500">{brl(sale.pending_amount)}</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Valor recebido (R$)</Label>
              <Input data-testid="settle-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className="num" />
            </div>
            <div className="space-y-1.5">
              <Label>Forma de pagamento</Label>
              <Select value={pmId} onValueChange={setPmId}>
                <SelectTrigger data-testid="settle-pm">
                  <SelectValue placeholder="Selecionar" />
                </SelectTrigger>
                <SelectContent>
                  {(pms || []).filter((p) => p.active).map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Vendedor responsável pelo recebimento *</Label>
              <Select value={sellerId} onValueChange={setSellerId}>
                <SelectTrigger data-testid="settle-seller">
                  <SelectValue placeholder="Selecionar vendedor" />
                </SelectTrigger>
                <SelectContent>
                  {(sellers || []).filter((s) => s.active).map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`settle-seller-${s.id}`}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="settle-confirm" onClick={save} disabled={saving} className="w-full h-11">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirmar recebimento
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Movimentacoes() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState("vendas");
  const [movOpen, setMovOpen] = useState(false);
  const [start, setStart] = useState(daysAgoISO(0));
  const [end, setEnd] = useState(todayISO());
  const [q, setQ] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [settleSale, setSettleSale] = useState(null);

  const { data: sales, isLoading: loadingSales } = useQuery({
    queryKey: ["sales", start, end, q],
    queryFn: async () => (await api.get("/sales", { params: { start, end, q } })).data,
  });
  const { data: movs, isLoading: loadingMovs } = useQuery({
    queryKey: ["movements", start, end],
    queryFn: async () => (await api.get("/movements", { params: { start, end } })).data,
  });

  const delSale = async () => {
    try {
      await api.delete(`/sales/${deleteId}`);
      toast.success("Venda excluída (estoque estornado)");
      qc.invalidateQueries();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="movimentacoes-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Movimentações</h1>
          <p className="text-sm text-muted-foreground">Vendas, entradas e saídas do caixa</p>
        </div>
        <Button data-testid="new-movement-btn" variant="outline" onClick={() => setMovOpen(true)}>
          <Plus className="mr-1 h-4 w-4" /> Entrada / Saída manual
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs">De</Label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="filter-start" className="w-40" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Até</Label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="filter-end" className="w-40" />
        </div>
        {tab === "vendas" && (
          <Input
            placeholder="Buscar por aluno ou item..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="filter-search"
            className="w-64"
          />
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="vendas" data-testid="tab-vendas">Vendas</TabsTrigger>
          <TabsTrigger value="movs" data-testid="tab-movs">Entradas / Saídas</TabsTrigger>
        </TabsList>

        <TabsContent value="vendas" className="mt-4">
          <Card>
            <CardContent className="overflow-x-auto p-0">
              {loadingSales ? (
                <div className="p-6 space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
              ) : (
                <Table data-testid="sales-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data/Hora</TableHead>
                      <TableHead>Aluno</TableHead>
                      <TableHead>Itens</TableHead>
                      <TableHead>Pagamento</TableHead>
                      <TableHead>Vendedor</TableHead>
                      <TableHead>Perfil</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(sales || []).map((s) => (
                      <TableRow key={s.id} data-testid={`sale-row-${s.id}`}>
                        <TableCell className="whitespace-nowrap">{fdatetime(s.created_at)}</TableCell>
                        <TableCell>{s.student_name || <span className="text-muted-foreground">Avulsa</span>}</TableCell>
                        <TableCell className="max-w-64 truncate">
                          {s.items.map((i) => `${i.qty > 1 ? i.qty + "x " : ""}${i.name}`).join(", ")}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{s.payment_method_name}</Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{s.seller_name || "—"}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{ROLE_LABELS[s.user_role] || s.user_name}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="num font-semibold text-emerald-500">{brl(s.total)}</div>
                          {s.pending_amount > 0 && (
                            <Badge variant="outline" className="mt-0.5 border-amber-500/40 text-amber-500" data-testid={`pending-badge-${s.id}`}>
                              Falta {brl(s.pending_amount)}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1 justify-end">
                            {s.pending_amount > 0 && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 border-amber-500/40 text-amber-500 hover:bg-amber-500/10"
                                data-testid={`settle-sale-${s.id}`}
                                onClick={() => setSettleSale(s)}
                              >
                                Quitar
                              </Button>
                            )}
                            {(user.role === "admin" || user.role === "gerente") && (
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-500"
                                onClick={() => setDeleteId(s.id)} data-testid={`delete-sale-${s.id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(sales || []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground">
                          Nenhuma venda no período.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="movs" className="mt-4">
          <Card>
            <CardContent className="overflow-x-auto p-0">
              {loadingMovs ? (
                <div className="p-6 space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
              ) : (
                <Table data-testid="movements-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data/Hora</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Categoria</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead>Pagamento</TableHead>
                      <TableHead>Responsável</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(movs || []).map((m) => (
                      <TableRow key={m.id} data-testid={`movement-row-${m.id}`}>
                        <TableCell className="whitespace-nowrap">{fdatetime(m.created_at)}</TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={m.type === "entrada"
                              ? "border-emerald-500/40 text-emerald-500"
                              : "border-red-500/40 text-red-500"}
                          >
                            {m.type === "entrada" ? "Entrada" : "Saída"}
                          </Badge>
                        </TableCell>
                        <TableCell>{m.category}</TableCell>
                        <TableCell className="text-muted-foreground">{m.description || "—"}</TableCell>
                        <TableCell className="text-muted-foreground">{m.payment_method_name}</TableCell>
                        <TableCell className="text-muted-foreground">{m.user_name}</TableCell>
                        <TableCell className={cn("num text-right font-semibold", m.type === "entrada" ? "text-emerald-500" : "text-red-500")}>
                          {m.type === "entrada" ? "+" : "−"}{brl(m.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {(movs || []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground">
                          Nenhuma movimentação manual no período.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <MovementDialog open={movOpen} onOpenChange={setMovOpen} />
      <SettleDialog sale={settleSale} onClose={() => setSettleSale(null)} />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-sale-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir venda?</AlertDialogTitle>
            <AlertDialogDescription>
              A venda será marcada como excluída (exclusão lógica, mantida na auditoria) e o estoque será estornado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-sale-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={delSale} data-testid="delete-sale-confirm">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
